import { useState } from "react";
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Loader2,
  X,
} from "lucide-react";

import type { ActionAlert } from "@/api/action_alerts";
import type { ActionAlertUrgency } from "@/lib/actionAlerts/dueDate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useActionAlerts,
  useDismissActionAlert,
  useDismissAllActionAlerts,
} from "@/hooks/useActionAlerts";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";

const URGENCY_SECTIONS: Array<{ id: ActionAlertUrgency; label: string }> = [
  { id: "overdue", label: "Needs attention" },
  { id: "urgent", label: "Due soon" },
  { id: "soon", label: "Coming up" },
];

function urgencyBadgeClass(urgency: ActionAlertUrgency): string {
  switch (urgency) {
    case "overdue":
      return "border-destructive/30 bg-destructive/10 text-destructive";
    case "urgent":
      return "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200";
    default:
      return "border-border bg-muted/40 text-muted-foreground";
  }
}

function AlertIcon({ alert }: { alert: ActionAlert }) {
  if (alert.kind === "task") {
    return <ClipboardList className="h-4 w-4 shrink-0 text-muted-foreground" />;
  }
  if (alert.urgency === "overdue") {
    return <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />;
  }
  return <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

function alertSubjectTitle(alert: ActionAlert): string {
  return alert.target?.title ?? alert.opportunity_title ?? alert.title;
}

type ActionAlertsSheetProps = {
  municipalityId: number | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectAlert: (alert: ActionAlert) => void;
};

export function ActionAlertsSheet({
  municipalityId,
  open,
  onOpenChange,
  onSelectAlert,
}: ActionAlertsSheetProps) {
  const { toast } = useToast();
  const { data, isLoading, isFetching, error, refetch, isRefetching } =
    useActionAlerts(municipalityId);
  const dismissMutation = useDismissActionAlert(municipalityId);
  const dismissAllMutation = useDismissAllActionAlerts(municipalityId);

  const alerts = data?.alerts ?? [];
  const grouped = URGENCY_SECTIONS.map((section) => ({
    ...section,
    alerts: alerts.filter((alert) => alert.urgency === section.id),
  })).filter((section) => section.alerts.length > 0);
  const showLoading = isLoading || (isFetching && alerts.length === 0);

  const notifyDismissError = () => {
    toast({
      title: "Could not dismiss alert",
      description: "Please try again in a moment.",
      variant: "destructive",
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b px-6 py-4 text-left">
          <SheetTitle>Action alerts</SheetTitle>
          <SheetDescription>
            Deadlines and milestones that need attention across your active work.
          </SheetDescription>
          {(data?.summary.total ?? 0) > 0 ? (
            <div className="flex items-center gap-2 pt-2">
              <Badge variant="secondary">{data?.summary.total ?? 0} active</Badge>
              {data?.summary.assigned_to_me ? (
                <Badge variant="outline">{data.summary.assigned_to_me} assigned to you</Badge>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-8 text-xs"
                disabled={dismissAllMutation.isPending}
                onClick={() =>
                  dismissAllMutation.mutate(undefined, { onError: notifyDismissError })
                }
              >
                Dismiss all
              </Button>
            </div>
          ) : null}
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {showLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading alerts…
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-sm text-destructive">Could not load alerts.</p>
              <Button
                variant="outline"
                size="sm"
                disabled={isRefetching}
                onClick={() => void refetch()}
              >
                {isRefetching ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Retrying…
                  </>
                ) : (
                  "Try again"
                )}
              </Button>
            </div>
          ) : alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-600/80" />
              <div>
                <p className="text-sm font-medium">You&apos;re all caught up</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  No overdue deadlines or upcoming milestones in the next two weeks.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {grouped.map((section) => (
                <section key={section.id} className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {section.label}
                  </h3>
                  <ul className="space-y-2">
                    {section.alerts.map((alert) => (
                      <li key={alert.key}>
                        <div
                          className={cn(
                            "flex items-start gap-3 rounded-lg border p-3",
                            urgencyBadgeClass(alert.urgency),
                          )}
                        >
                          <AlertIcon alert={alert} />
                          <button
                            type="button"
                            className="min-w-0 flex-1 text-left"
                            onClick={() => onSelectAlert(alert)}
                          >
                            <p className="text-sm font-medium leading-snug">{alert.title}</p>
                            <p className="mt-1 line-clamp-2 text-xs opacity-90">{alert.body}</p>
                            {alert.assigned_to_me ? (
                              <p className="mt-1 text-[11px] font-medium opacity-80">Assigned to you</p>
                            ) : null}
                          </button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            aria-label={`Dismiss alert for ${alertSubjectTitle(alert)}`}
                            disabled={
                              dismissMutation.isPending &&
                              dismissMutation.variables === alert.key
                            }
                            onClick={() =>
                              dismissMutation.mutate(alert.key, { onError: notifyDismissError })
                            }
                          >
                            {dismissMutation.isPending &&
                            dismissMutation.variables === alert.key ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <X className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>

        {data?.summary.truncated ? (
          <div className="border-t px-6 py-3 text-center text-xs text-muted-foreground">
            Showing {alerts.length} of {data.summary.total} alerts
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

type ActionAlertsBellProps = {
  municipalityId: number | undefined;
  onSelectAlert: (alert: ActionAlert) => void;
  enabled?: boolean;
};

export function ActionAlertsBell({
  municipalityId,
  onSelectAlert,
  enabled = true,
}: ActionAlertsBellProps) {
  const [open, setOpen] = useState(false);
  const { data } = useActionAlerts(municipalityId, { poll: true, enabled });

  const count = data?.summary.total ?? 0;
  const overdue = data?.summary.overdue ?? 0;

  if (!enabled) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="relative w-full justify-start"
        onClick={() => setOpen(true)}
        disabled={!municipalityId}
        aria-label={count > 0 ? `${count} action alerts` : "Action alerts"}
        data-testid="action-alerts-bell"
      >
        <Bell className="h-4 w-4" />
        {count > 0 ? (
          <span
            className={cn(
              "absolute right-2 top-1/2 inline-flex h-4 min-w-4 -translate-y-1/2 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white",
              overdue > 0 ? "bg-destructive" : "bg-primary",
            )}
          >
            {count > 99 ? "99+" : count}
          </span>
        ) : null}
        <span className="ml-2">Alerts</span>
      </Button>

      <ActionAlertsSheet
        municipalityId={municipalityId}
        open={open}
        onOpenChange={setOpen}
        onSelectAlert={(alert) => {
          setOpen(false);
          onSelectAlert(alert);
        }}
      />
    </>
  );
}
